import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addCorpusCompany,
  approveCorpusCompanies,
  confirmCorpusRole,
  findCorpusContacts,
  findPeopleForCompanies,
  getCorpusHistory,
  getEmailSettings,
  getHunterCredits,
  importCorpusCompanies,
  listCorpusCompanies,
  listCorpusContacts,
  markSendListSent,
  refreshCorpusCoverage,
  previewSendToCompanies,
  reviewCorpusCompany,
  setCorpusPriority,
  verifyCorpusRoles,
  sendInvitationsToCompanies,
} from '../api/client';
import { useCurrentUser } from '../auth/AuthContext';
import { parseCsvObjects } from '../corpus/csv';
import EmailTemplateModal from '../corpus/EmailTemplateModal';
import { INVITATION_LABELS, InvitationTab, SyncHealth } from '../corpus/Invitations';
import './Corpus.css';
import { X, Plus } from 'lucide-react';

const FIT_LABELS = {
  likely_fit: 'Likely fit',
  needs_review: 'Needs review',
  not_a_fit: 'Not a fit',
  not_live: 'Site not live',
};
const PRIORITY_ORDER = { first: 0, backup: 1, none: 2 };
const PAGE_SIZE = 15;

// The company's invitation state, grouped the way the tabs count it.
function invitationGroup(company) {
  const state = company.invitation ? company.invitation.state : null;
  if (state === 'registered' || state === 'registered_revoked') return 'registered';
  if (state === 'revoked') return 'not_invited'; // every link withdrawn: nobody holds a live one
  return state;
}

const ROLE_LABELS = {
  technology: 'Technology lead',
  data: 'Data owner',
  operations: 'Operations lead',
  founder: 'Founder or owner',
  executive: 'CEO or managing director',
  digital: 'Digital or product lead',
};

function reviewState(company) {
  return company.review_decision || 'pending';
}

function Link({ href, children }) {
  if (!href) return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
}

function Credits({ credits }) {
  if (!credits) return <span className="corpus-credit-note">Checking Hunter…</span>;
  if (!credits.configured) return <span className="corpus-credit-note">Hunter is not set up: add the key to backend/.env.</span>;
  if (credits.error) return <span className="corpus-credit-note">Hunter credits unavailable ({credits.error}).</span>;
  if (credits.used == null || credits.available == null) return <span className="corpus-credit-note">Hunter is connected.</span>;
  return (
    <span className="corpus-credit-note">
      Hunter credits: <strong>{credits.used}</strong> of <strong>{credits.available}</strong> used
    </span>
  );
}

function EvidenceTab({ company }) {
  const { fit, list } = company;
  return (
    <>
      <h4>WHY IT LOOKS LIKE A FIT</h4>
      <p className="corpus-reason">{fit.reason}</p>
      {company.data_possibility && (
        <>
          <h4>BUSINESS DATA IT MAY HAVE · A POSSIBILITY, NOT CONFIRMED</h4>
          <p className="corpus-possibility">{company.data_possibility}</p>
        </>
      )}
      <h4>UAE EVIDENCE</h4>
      <p>{fit.uae_evidence ? <Link href={fit.uae_source}>{fit.uae_evidence}</Link> : 'None found on the pages read.'}</p>
      <h4>SECTOR EVIDENCE{fit.sector_guess ? ` · ${fit.sector_guess.toUpperCase()}` : ''}</h4>
      <p>{fit.sector_evidence ? <Link href={fit.sector_source}>{fit.sector_evidence}</Link> : 'None found on the pages read.'}</p>
      <h4>LANGUAGE</h4>
      <p>{fit.bilingual ? 'Arabic and English found' : 'Only one language found'}</p>
      {list && list.categories && (
        <>
          <h4>IN THE SOURCE LIST AS</h4>
          <p>{list.categories}{list.city ? ` · ${list.city}` : ''}</p>
        </>
      )}
      <h4>CHECKED</h4>
      <p>{fit.checked_at ? new Date(fit.checked_at).toLocaleDateString() : 'Not recorded'}</p>
    </>
  );
}

// "http://linkedin.com/in/x" and "linkedin.com/in/x" both become an https link.
function normalizeUrl(value) {
  const text = value.trim();
  if (!text) return '';
  if (/^https:\/\//i.test(text)) return text;
  if (/^http:\/\//i.test(text)) return `https://${text.slice(7)}`;
  return `https://${text}`;
}

function urlProblem(value) {
  const url = normalizeUrl(value);
  if (!url) return 'Paste the page that shows the role.';
  if (/google\./i.test(url) || /\/search\?/i.test(url)) return 'A search page is not enough. Use the direct page of the person.';
  if (url.length < 12 || !url.includes('.')) return 'This does not look like a web page address.';
  return '';
}

function RoleEvidence({ evidence, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [exactTitle, setExactTitle] = useState('');
  const [saving, setSaving] = useState(false);
  if (evidence && evidence.status === 'confirmed') {
    const manual = evidence.source === 'manual';
    return (
      <div className="corpus-role confirmed">
        {manual ? 'Role confirmed by hand: ' : 'Role confirmed on the company site: '}
        <Link href={evidence.url}>{evidence.url}</Link>
        {evidence.quote && <div className="corpus-quote">“{evidence.quote}”</div>}
      </div>
    );
  }
  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await onConfirm(normalizeUrl(url), note.trim(), exactTitle.trim());
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="corpus-role review">
      Role needs review: {(evidence && evidence.note) || 'not checked yet.'}
      {!open ? (
        <div><button type="button" onClick={() => setOpen(true)}>Confirm role by hand</button></div>
      ) : (
        <form className="corpus-manual" onSubmit={submit}>
          <input aria-label="Page that shows the role" placeholder="Page that shows the role, e.g. linkedin.com/in/name" value={url} onChange={(e) => setUrl(e.target.value)} />
          <input aria-label="What the page shows" placeholder="What it shows, e.g. LinkedIn: CTO at Wavetec" value={note} onChange={(e) => setNote(e.target.value)} />
          <input aria-label="Exact title on that page" placeholder="Exact title on that page (optional)" value={exactTitle} onChange={(e) => setExactTitle(e.target.value)} />
          <button type="submit" disabled={saving || Boolean(urlProblem(url)) || note.trim().length < 5}>Save</button>
          {(urlProblem(url) || note.trim().length < 5) && (
            <div className="corpus-hint-small">{urlProblem(url) || 'Write a few words about what the page shows.'}</div>
          )}
          <button type="button" onClick={() => setOpen(false)}>Cancel</button>
        </form>
      )}
    </div>
  );
}

function PeopleTab({ company, people, loading, searching, checking, confirming, note, onAsk, onCancel, onConfirm, onVerify, onConfirmRole, onPriority }) {
  if (reviewState(company) !== 'approved') {
    return <div className="state-msg">Approve this company first. People are only searched for approved companies.</div>;
  }
  const cover = company.coverage;
  const blocked = cover && (cover.personal || 0) === 0;
  return (
    <>
      <p className="corpus-hint">
        Looks for technology, data, operations and product leads and founders with a valid work email. A CEO is listed only when there are fewer than three people in those roles.
        {cover ? ` Hunter knows ${cover.personal || 0} ${(cover.personal || 0) === 1 ? 'person' : 'people'} here.` : ''}
      </p>
      <div className="corpus-actions">
        {!confirming ? (
          <button type="button" className="approve" disabled={searching || blocked} onClick={onAsk}>
            {searching ? 'Searching…' : blocked ? 'Hunter knows nobody here' : 'Find people'}
          </button>
        ) : (
          <>
            <button type="button" className="approve" onClick={onConfirm}>Yes, use about 1 Hunter credit</button>
            <button type="button" onClick={onCancel}>Cancel</button>
          </>
        )}
        {people.length > 0 && (
          <button type="button" disabled={checking} onClick={onVerify}>
            {checking ? 'Checking the company site…' : 'Check roles on the company site (free)'}
          </button>
        )}
      </div>
      {note && <div className="corpus-notice" role="status">{note}</div>}
      {loading ? (
        <div className="state-msg">Loading people…</div>
      ) : people.length === 0 ? (
        <div className="state-msg">No people saved for this company yet.</div>
      ) : (
        <ul className="corpus-people">
          {[...people].sort((a, b) => PRIORITY_ORDER[a.priority || 'none'] - PRIORITY_ORDER[b.priority || 'none']).map((person) => (
            <li key={person.id} className={person.priority ? `priority-${person.priority}` : ''}>
              <strong>{person.full_name}</strong>
              {person.priority && <span className={`corpus-badge priority-${person.priority}`}>{person.priority === 'first' ? 'First contact' : 'Backup'}</span>}
              <span className="corpus-badge">{ROLE_LABELS[person.role_group] || person.role_group}</span>
              <div>{person.title}</div>
              <div className="corpus-email">{person.email}</div>
              <div className="corpus-line">
                Hunter status: {person.hunter_status}
                {person.hunter_checked_on ? ` · Hunter's stored check ${person.hunter_checked_on}` : ''}
                {' '}(not re-verified by us)
              </div>
              <RoleEvidence evidence={person.role_evidence} onConfirm={(url, note, title) => onConfirmRole(person, url, note, title)} />
              <div className="corpus-priority">
                {person.priority !== 'first' && <button type="button" onClick={() => onPriority(person, 'first')}>Set as first contact</button>}
                {person.priority !== 'backup' && <button type="button" onClick={() => onPriority(person, 'backup')}>Set as backup</button>}
                {person.priority && <button type="button" onClick={() => onPriority(person, null)}>Clear</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const ACTION_TEXT = {
  approved: () => 'approved the company',
  dismissed: () => 'dismissed the company',
  company_imported: () => 'imported the company',
  company_added: () => 'added the company',
  evidence_edited: (d) => `corrected the evidence${d.fields ? ` (${d.fields.join(', ')})` : ''}`,
  data_possibility_set: () => 'wrote the sentence about the data it may have',
  roles_checked: (d) => `checked roles on the company site (${d.confirmed || 0} of ${d.checked || 0} confirmed)`,
  people_searched: (d) => `searched for people (${d.found || 0} found)`,
  role_confirmed_by_hand: (d) => `confirmed the role of ${d.person} by hand`,
  priority_set: (d) => (d.priority ? `set ${d.person} as ${d.priority === 'first' ? 'the first contact' : 'a backup'}` : `cleared the priority of ${d.person}`),
  contact_approved: (d) => `approved ${d.person}`,
  contact_dismissed: (d) => `dismissed ${d.person}`,
  contact_deleted: (d) => `deleted ${d.person}`,
  invitation_created: (d) => `created an invitation link for ${d.person}`,
  invitation_sent: (d) => `marked the invitation to ${d.person} as sent`,
  invitation_revoked: (d) => `revoked the invitation to ${d.person}`,
  menaos_registered: (d) => `recorded a registration through ${d.revoked_link ? 'a revoked' : 'an invitation'} link (MenaOS ${d.reference})`,
  menaos_attached_by_hand: (d) => `attached the registration MenaOS ${d.reference} to an invitation by hand`,
  companies_merged: (d) => `merged ${d.duplicate_name} (${d.duplicate_domain || 'no website'}) into this company`,
  merge_rejected: () => 'rejected a suggestion to merge this company with another',
};

function formatWhen(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function HistoryTab({ companyId, refreshKey }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    getCorpusHistory(companyId)
      .then((r) => { if (!cancelled) setItems(r.items); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [companyId, refreshKey]);
  if (error) return <div className="corpus-error" role="alert">{error}</div>;
  if (!items) return <div className="state-msg">Loading history…</div>;
  if (items.length === 0) return <div className="state-msg">Nothing has been decided on this company yet.</div>;
  return (
    <ul className="corpus-history">
      {items.map((event) => {
        const describe = ACTION_TEXT[event.action];
        return (
          <li key={event.id}>
            <strong>{event.user_name}</strong> {describe ? describe(event.detail || {}) : event.action}
            <div className="corpus-line">{formatWhen(event.created_at)}</div>
            {event.detail && event.detail.moved_from && (
              <div className="corpus-line">Made on {event.detail.moved_from.name}{event.detail.moved_from.domain ? ` (${event.detail.moved_from.domain})` : ''}, before the merge</div>
            )}
            {event.detail && event.detail.reason && <div className="corpus-line">Reason: {event.detail.reason}</div>}
            {event.detail && event.detail.note && <div className="corpus-line">Note: {event.detail.note}</div>}
          </li>
        );
      })}
    </ul>
  );
}

function CompanyDrawer({ company, onClose, onReview, onPeopleChanged, onInvitationChanged }) {
  const [tab, setTab] = useState(company.review_decision === 'approved' ? 'invitation' : 'evidence');
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [searching, setSearching] = useState(false);
  const [checking, setChecking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const state = reviewState(company);

  useEffect(() => {
    setTab(company.review_decision === 'approved' ? 'invitation' : 'evidence');
    setDismissing(false);
    setConfirming(false);
    setNote('');
    setError('');
    // Only when another company is opened: approving this one must not jump to another tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  useEffect(() => {
    let cancelled = false;
    if (state !== 'approved') {
      setPeople([]);
      return undefined;
    }
    setLoadingPeople(true);
    listCorpusContacts(company.id)
      .then((r) => { if (!cancelled) setPeople(r.items); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoadingPeople(false); });
    return () => { cancelled = true; };
  }, [company.id, state]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function search() {
    setConfirming(false);
    setSearching(true);
    setError('');
    setNote('');
    try {
      const result = await findCorpusContacts(company.id);
      setNote(result.note);
      setPeople((await listCorpusContacts(company.id)).items);
      onPeopleChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  }

  async function setPriority(person, priority) {
    setError('');
    try {
      setPeople((await setCorpusPriority(company.id, person.id, priority)).items);
    } catch (e) {
      setError(e.message);
    }
  }

  async function confirmRole(person, url, note, title) {
    setError('');
    try {
      const updated = await confirmCorpusRole(company.id, person.id, url, note, title);
      setPeople((current) => current.map((p) => (p.id === person.id ? updated : p)));
    } catch (e) {
      setError(e.message);
    }
  }

  async function verify() {
    setChecking(true);
    setError('');
    try {
      const result = await verifyCorpusRoles(company.id);
      setNote(`Checked ${result.checked} ${result.checked === 1 ? 'person' : 'people'} on the company site: ${result.confirmed} confirmed, ${result.needs_review} need review.`);
      setPeople((await listCorpusContacts(company.id)).items);
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      <div className="overlay show" data-testid="company-popup-backdrop" onClick={onClose} />
      {/* A popup in the middle of the page, not a side panel. Escape and the backdrop close it. */}
      <div className="drawer corpus-modal corpus-v2-company show" role="dialog" aria-modal="true" aria-label={`${company.name} details`}>
        <div className="drawer-head corpus-v2-company-head">
          <div className="corpus-v2-avatar" aria-hidden="true">{(company.name || '?').slice(0, 1).toUpperCase()}</div>
          <div className="corpus-v2-company-title">
            <div className="drawer-title">{company.name}</div>
            <div className="corpus-v2-company-meta">
              <Link href={company.fit.final_url || `https://${company.domain}`}>{company.domain}</Link>
              <span className={`corpus-badge decision-${state}`}>
                {state === 'pending' ? 'Waiting for review' : state === 'approved' ? 'Approved' : 'Dismissed'}
              </span>
              {company.invitation && state === 'approved' && (
                <span className={`corpus-badge invite-${company.invitation.state}`}>{INVITATION_LABELS[company.invitation.state]}</span>
              )}
              <span className={`corpus-badge fit-${company.fit.status}`} title="What the website check found">{FIT_LABELS[company.fit.status] || company.fit.status}</span>
              {company.reviewed_by && <span className="corpus-muted">{state === 'dismissed' ? 'Dismissed' : 'Approved'} by {company.reviewed_by}</span>}
            </div>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Close"><X size={18} aria-hidden="true" /></button>
        </div>
        <div className="drawer-body">
          {state === 'pending' && !dismissing && (
            <div className="corpus-actions">
              <button type="button" className="approve" onClick={() => onReview(company, 'approved')}>Approve</button>
              <button type="button" className="dismiss" onClick={() => { setDismissing(true); setReason(''); }}>Dismiss</button>
            </div>
          )}
          {dismissing && (
            <form className="corpus-actions" onSubmit={(e) => { e.preventDefault(); onReview(company, 'dismissed', reason); setDismissing(false); }}>
              <input aria-label="Reason for dismissing" placeholder="Short reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button type="submit" className="dismiss" disabled={!reason.trim()}>Confirm dismiss</button>
              <button type="button" onClick={() => setDismissing(false)}>Cancel</button>
            </form>
          )}
          {state === 'dismissed' && (
            <div className="corpus-decision">
              Dismissed: {company.review_reason}
              <button type="button" onClick={() => onReview(company, 'approved')}>Approve instead</button>
            </div>
          )}

          <div className="corpus-tabs corpus-v2-company-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'invitation'} className={tab === 'invitation' ? 'active' : ''} onClick={() => setTab('invitation')}>People &amp; invitations</button>
            <button type="button" role="tab" aria-selected={tab === 'people'} className={tab === 'people' ? 'active' : ''} onClick={() => setTab('people')}>
              Find people{people.length ? ` (${people.length})` : ''}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'evidence'} className={tab === 'evidence' ? 'active' : ''} onClick={() => setTab('evidence')}>Evidence</button>
            <button type="button" role="tab" aria-selected={tab === 'history'} className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>History</button>
          </div>
          {error && <div className="corpus-error" role="alert">{error}</div>}
          {tab === 'evidence' && <EvidenceTab company={company} />}
          {tab === 'history' && <HistoryTab companyId={company.id} refreshKey={`${company.review_decision}-${people.length}-${note}-${company.invitation ? company.invitation.state : ''}`} />}
          {tab === 'invitation' && <InvitationTab company={company} people={people} onChanged={onInvitationChanged} />}
          {tab === 'people' && (
            <PeopleTab
              company={company}
              people={people}
              loading={loadingPeople}
              searching={searching}
              checking={checking}
              confirming={confirming}
              note={note}
              onAsk={() => setConfirming(true)}
              onCancel={() => setConfirming(false)}
              onConfirm={search}
              onVerify={verify}
              onConfirmRole={confirmRole}
              onPriority={setPriority}
            />
          )}
        </div>
      </div>
    </>
  );
}


// The list's tabs: one row instead of the three filter rows. "Waiting review" only shows when a
// company still needs a decision (the automatic flow will only bring approved ones).
const TABS = [
  ['all', 'All'],
  ['ready', 'Ready to send'],
  ['sent', 'Sent'],
  ['review', 'Needs review'],
  ['registered', 'Registered'],
  ['waiting', 'Waiting review'],
];

function companyTab(company) {
  if (reviewState(company) !== 'approved') return reviewState(company) === 'pending' ? 'waiting' : 'dismissed';
  const state = company.invitation ? company.invitation.state : 'not_invited';
  if (state === 'review_needed' || state === 'review_rejected') return 'review';
  const group = invitationGroup(company);
  if (group === 'registered') return 'registered';
  if (group === 'sent') return 'sent';
  return 'ready';
}

function shortDate(value) {
  if (!value) return '–';
  return new Date(value).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

// Page numbers to show: first, last, and the pages around the current one.
function pageNumbers(current, count) {
  const keep = new Set([1, count, current - 1, current, current + 1]);
  const list = [];
  for (let n = 1; n <= count; n += 1) {
    if (keep.has(n)) list.push(n);
    else if (list[list.length - 1] !== '…') list.push('…');
  }
  return list;
}

export default function Corpus() {
  const [companies, setCompanies] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const user = useCurrentUser();
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState('');
  const [openId, setOpenId] = useState(null);
  const [markConfirm, setMarkConfirm] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [findConfirm, setFindConfirm] = useState(false);
  const [sending, setSending] = useState(null); // the send preview while the email popup is open
  const [emailSettings, setEmailSettings] = useState(null);
  const fileInput = useRef(null);

  const refreshCredits = useCallback(() => {
    getHunterCredits().then(setCredits).catch(() => setCredits({ configured: true, error: 'request failed' }));
  }, []);

  // After an invitation changes, refresh the list quietly so its column and counts follow.
  const reloadList = useCallback(async () => {
    try {
      setCompanies((await listCorpusCompanies()).items);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let result = await listCorpusCompanies();
      if (result.items.some((c) => !c.coverage)) {
        // Free Hunter call (no credits): how many people Hunter knows at each company.
        const filled = await refreshCorpusCoverage().catch(() => null);
        if (filled && filled.checked > 0) result = await listCorpusCompanies();
      }
      setCompanies(result.items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    refreshCredits();
    getEmailSettings().then(setEmailSettings).catch(() => setEmailSettings({ configured: false }));
  }, [load, refreshCredits]);

  const tabCounts = useMemo(() => {
    const tally = { all: companies.length, ready: 0, sent: 0, review: 0, registered: 0, waiting: 0, dismissed: 0 };
    companies.forEach((c) => { tally[companyTab(c)] += 1; });
    return tally;
  }, [companies]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return companies.filter(
      (c) => (tab === 'all' || companyTab(c) === tab)
        && (!q || `${c.name} ${c.domain || ''}`.toLowerCase().includes(q)),
    );
  }, [companies, tab, query]);

  // 15 companies per page. The selection is kept across pages, so a batch can span several.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [tab, query]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstShown = (currentPage - 1) * PAGE_SIZE;
  const shown = visible.slice(firstShown, firstShown + PAGE_SIZE);

  const approvedCount = companies.filter((c) => reviewState(c) === 'approved').length;
  const readyPeople = companies.filter((c) => companyTab(c) === 'ready').reduce((n, c) => n + (c.people || 0), 0);
  const selectedCompanies = companies.filter((c) => selected.has(c.id));
  const selectedPeople = selectedCompanies.reduce((n, c) => n + (c.people || 0), 0);
  const selectedWaiting = selectedCompanies.some((c) => reviewState(c) !== 'approved');

  const openCompany = companies.find((c) => c.id === openId) || null;

  async function handleImport(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setBusy('import');
    setError('');
    setNotice('');
    try {
      const rows = parseCsvObjects(await file.text()).filter((row) => row.domain);
      if (!rows.length) throw new Error('No companies found. The file needs a "domain" column.');
      const result = await importCorpusCompanies(rows);
      const skipped = result.skipped_buyer ? ` ${result.skipped_buyer} already belong to AI-buyer companies and were left alone.` : '';
      setNotice(`Imported: ${result.created} new, ${result.updated} refreshed.${skipped}`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleAdd(event) {
    event.preventDefault();
    if (!newDomain.trim()) return;
    setBusy('add');
    setError('');
    setNotice('');
    try {
      const result = await addCorpusCompany(newDomain.trim());
      setNotice(`${result.company.name} was checked and added.`);
      setNewDomain('');
      setAdding(false);
      await load();
      setOpenId(result.company.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleMarkAllSent() {
    setBusy('mark');
    setError('');
    setNotice('');
    try {
      const result = await markSendListSent();
      setNotice(`Marked ${result.sent} invitation${result.sent === 1 ? '' : 's'} as sent.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
      setMarkConfirm(false);
    }
  }

  async function handleReview(company, decision, reason) {
    setError('');
    try {
      const updated = await reviewCorpusCompany(company.id, decision, reason);
      setCompanies((current) => current.map((c) => (c.id === company.id ? { ...c, ...updated } : c)));
    } catch (err) {
      setError(err.message);
    }
  }

  // --- selecting companies across pages ---
  const shownIds = shown.map((c) => c.id);
  const shownSelected = shownIds.filter((id) => selected.has(id)).length;
  const allShownSelected = shownIds.length > 0 && shownSelected === shownIds.length;

  function toggleSelect(id) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAllShown() {
    setSelected((current) => {
      const next = new Set(current);
      if (allShownSelected) shownIds.forEach((id) => next.delete(id));
      else shownIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
    setFindConfirm(false);
  }

  async function handleBulkApprove() {
    setBusy('bulk');
    setError('');
    setNotice('');
    try {
      const result = await approveCorpusCompanies([...selected]);
      setNotice(`Approved ${result.approved} ${result.approved === 1 ? 'company' : 'companies'}.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleBulkFind() {
    setBusy('bulk');
    setError('');
    setNotice('');
    try {
      const result = await findPeopleForCompanies([...selected]);
      const skipped = result.skipped ? ` ${result.skipped} skipped (approve them first).` : '';
      const failed = result.failed ? ` ${result.failed} failed at Hunter.` : '';
      setNotice(`Searched ${result.searched} ${result.searched === 1 ? 'company' : 'companies'}: ${result.new} new people, ${result.credits_used} Hunter ${result.credits_used === 1 ? 'credit' : 'credits'} used.${skipped}${failed}`);
      await load();
      refreshCredits();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
      setFindConfirm(false);
    }
  }

  async function openSend() {
    setBusy('preview');
    setError('');
    try {
      const preview = await previewSendToCompanies([...selected]);
      if (!preview.recipients.length) {
        setError(`Nobody to email at the selected companies${preview.skipped.length ? ` (${[...new Set(preview.skipped.map((s) => s.reason))].join('; ')})` : ''}.`);
        return;
      }
      setSending(preview);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleBulkSend() {
    setBusy('send');
    setError('');
    setNotice('');
    try {
      const r = await sendInvitationsToCompanies([...selected]);
      const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
      const parts = [`Sent ${plural(r.sent, 'email', 'emails')}.`];
      if (r.skipped.length) parts.push(`${plural(r.skipped.length, 'skipped', 'skipped')} (${[...new Set(r.skipped.map((s) => s.reason))].join('; ')})`);
      if (r.failed.length) parts.push(`${plural(r.failed.length, 'failed', 'failed')}: ${r.failed.map((f) => `${f.person || f.company} (${f.reason})`).join('; ')}`);
      if (r.stopped_early) parts.push(`Stopped: ${r.stopped_early}`);
      (r.failed.length || r.stopped_early ? setError : setNotice)(parts.join(' '));
      if (r.sent && !r.failed.length) setSelected(new Set());
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
      setSending(null);
    }
  }

  const canEmail = Boolean(emailSettings && emailSettings.configured);

  return (
    <div className="corpus-page corpus-v2">
      <div className="corpus-v2-head">
        <div>
          <div className="corpus-eyebrow">COMPANY CORPUS · UAE</div>
          <h2>Data suppliers</h2>
        </div>
        <div className="corpus-v2-actions">
          <SyncHealth isAdmin={Boolean(user && user.role === 'admin')} />
          <Credits credits={credits} />
          <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={handleImport} />
          <button type="button" className="v2-button" disabled={busy !== ''} onClick={() => fileInput.current && fileInput.current.click()}>
            {busy === 'import' ? 'Importing…' : 'Import CSV'}
          </button>
          <details className="v2-more">
            <summary className="v2-button">More</summary>
            <div className="v2-more-menu">
              <a href="/api/v1/corpus/export" download="corpus-results.csv">Export CSV</a>
              <a href="/api/v1/corpus/send-list" download="corpus-send-list.csv">Download send list (mail merge)</a>
              {!markConfirm ? (
                <button type="button" disabled={busy !== ''} onClick={() => setMarkConfirm(true)}>Mark all as sent</button>
              ) : (
                <button type="button" className="v2-danger" disabled={busy !== ''} onClick={handleMarkAllSent}>
                  {busy === 'mark' ? 'Marking…' : 'Confirm mark all sent'}
                </button>
              )}
            </div>
          </details>
          <button type="button" className="v2-button v2-primary" onClick={() => setAdding((v) => !v)} aria-expanded={adding}><Plus size={16} aria-hidden="true" />Add company</button>
        </div>
      </div>

      {adding && (
        <form className="corpus-v2-add" onSubmit={handleAdd}>
          <label>
            Company website
            <input placeholder="example.ae" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
          </label>
          <button type="submit" className="v2-button v2-primary" disabled={busy !== '' || !newDomain.trim()}>{busy === 'add' ? 'Checking…' : 'Check and add'}</button>
          <button type="button" className="v2-button" onClick={() => setAdding(false)}>Cancel</button>
        </form>
      )}

      <section className="corpus-v2-cards" aria-label="Progress">
        <div><span>Approved companies</span><strong>{approvedCount}</strong><small>ready for outreach</small></div>
        <div><span>Ready to send</span><strong>{tabCounts.ready}</strong><small>{readyPeople} {readyPeople === 1 ? 'person' : 'people'} at these companies</small></div>
        <div><span>Sent</span><strong>{tabCounts.sent}</strong><small>companies emailed</small></div>
        <div><span>Registered</span><strong>{tabCounts.registered}</strong><small>applied on MenaOS</small></div>
      </section>

      {error && <div className="corpus-error" role="alert">{error}</div>}
      {notice && <div className="corpus-notice" role="status">{notice}</div>}

      <section className="corpus-v2-list" aria-label="Companies">
        <div className="corpus-v2-listbar">
          <div className="corpus-v2-tabs" role="tablist" aria-label="Invitation status">
            {TABS.filter(([key]) => key !== 'waiting' || tabCounts.waiting > 0).map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
                {label} <span>{tabCounts[key]}</span>
              </button>
            ))}
          </div>
          <label className="corpus-v2-search">
            <span className="v2-hidden">Search companies</span>
            <input type="search" placeholder="Search company or domain" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
        </div>

        {loading ? (
          <div className="corpus-state">Loading companies…</div>
        ) : companies.length === 0 ? (
          <div className="corpus-state">No companies yet. Import a CSV or add one company.</div>
        ) : visible.length === 0 ? (
          <div className="corpus-state">No companies match.</div>
        ) : (
          <div className="corpus-table corpus-v2-table" role="table" aria-label="Companies">
            <div className="corpus-row corpus-row-head" role="row">
              <span role="columnheader" className="corpus-select-cell">
                <input type="checkbox" aria-label="Select all on this page" checked={allShownSelected} onChange={toggleSelectAllShown} />
              </span>
              <span role="columnheader">Company</span>
              <span role="columnheader">People</span>
              <span role="columnheader">Invitation</span>
              <span role="columnheader">Last activity</span>
            </div>
            {shown.map((company) => {
              const state = reviewState(company);
              const invite = company.invitation ? company.invitation.state : null;
              return (
                <div
                  key={company.id}
                  role="row"
                  tabIndex={0}
                  className={`corpus-row corpus-row-item${selected.has(company.id) ? ' picked' : ''}${openId === company.id ? ' open' : ''}`}
                  onClick={() => setOpenId(company.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenId(company.id); } }}
                >
                  <span className="corpus-select-cell">
                    <input
                      type="checkbox"
                      aria-label={`Select ${company.name}`}
                      checked={selected.has(company.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(company.id)}
                    />
                  </span>
                  <span className="corpus-cell-main">
                    <strong>{company.name}</strong>
                    <small>{state === 'approved' ? `Approved${company.reviewed_by ? ` by ${company.reviewed_by}` : ''}` : company.domain}</small>
                  </span>
                  <span className="v2-mono">{company.people || 0}</span>
                  <span>
                    {state !== 'approved' ? (
                      <span className={`corpus-badge decision-${state}`}>{state === 'pending' ? 'Waiting review' : 'Dismissed'}</span>
                    ) : invite ? (
                      <span className={`corpus-badge invite-${invite}`} title={company.invitation.reference ? `MenaOS ${company.invitation.reference}` : undefined}>
                        {INVITATION_LABELS[invite]}
                      </span>
                    ) : <span className="corpus-muted">–</span>}
                  </span>
                  <span className="corpus-muted">{shortDate(company.last_activity_at)}</span>
                </div>
              );
            })}
          </div>
        )}

        {!loading && visible.length > 0 && (
          <nav className="corpus-pager" aria-label="Pages">
            <span>
              <strong>{firstShown + 1}–{firstShown + shown.length}</strong> of {visible.length}
            </span>
            {pageCount > 1 && (
              <div className="corpus-pager-buttons">
                <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>← Previous</button>
                {pageNumbers(currentPage, pageCount).map((n, i) => (n === '…' ? (
                  <span key={`gap-${i}`} className="corpus-muted">…</span>
                ) : (
                  <button key={n} type="button" aria-current={n === currentPage ? 'page' : undefined} className={n === currentPage ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
                )))}
                <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>Next →</button>
              </div>
            )}
          </nav>
        )}
      </section>

      {selected.size > 0 && (
        <div className="corpus-v2-bulkbar" role="region" aria-label="Actions for selected companies">
          <span><strong>{selected.size}</strong> {selected.size === 1 ? 'company' : 'companies'} · <strong>{selectedPeople}</strong> {selectedPeople === 1 ? 'person' : 'people'}</span>
          <button type="button" className="v2-ghost" onClick={clearSelection}>Clear</button>
          {selectedWaiting && (
            <button type="button" className="v2-ghost" disabled={busy !== ''} onClick={handleBulkApprove}>
              {busy === 'bulk' ? 'Working…' : 'Approve'}
            </button>
          )}
          {!findConfirm ? (
            <button type="button" className="v2-ghost" disabled={busy !== ''} onClick={() => setFindConfirm(true)}>Find people</button>
          ) : (
            <button type="button" className="v2-ghost" disabled={busy !== ''} onClick={handleBulkFind}>
              {busy === 'bulk' ? 'Searching…' : `Confirm: up to ${selected.size} Hunter ${selected.size === 1 ? 'credit' : 'credits'}`}
            </button>
          )}
          <button
            type="button"
            className="v2-send"
            disabled={busy !== '' || !canEmail}
            title={!canEmail ? 'Email sending is not set up yet' : undefined}
            onClick={openSend}
          >
            {busy === 'preview' ? 'Checking…' : 'Send email…'}
          </button>
        </div>
      )}

      {sending && (
        <EmailTemplateModal
          title="Check the email before it goes out"
          recipients={sending.recipients}
          companies={sending.companies}
          skipped={sending.skipped}
          confirmLabel={`Confirm and send to ${sending.recipients.length} ${sending.recipients.length === 1 ? 'person' : 'people'}`}
          onConfirm={handleBulkSend}
          onClose={() => setSending(null)}
        />
      )}

      {openCompany && (
        <CompanyDrawer
          company={openCompany}
          onClose={() => setOpenId(null)}
          onReview={handleReview}
          onPeopleChanged={refreshCredits}
          onInvitationChanged={reloadList}
        />
      )}
    </div>
  );
}
